// frontend/src/components/ImpersonationBanner.js
//
// Bandeau fixe affiché EN HAUT de toutes les pages quand un owner pilote le
// dashboard d'un autre compte (via /api/owner/impersonate).
//
// Source de vérité : storage.get('revyo_impersonation') — JSON sérialisé
// { id, nom, expiresAt }, posé par OwnerDashboard.handleImpersonate après
// le swap du JWT actif.
//
// Re-lecture : à chaque changement de route (dépendance useLocation), pour
// rafraîchir l'état si l'utilisateur démarre/sort d'une session pilotage
// depuis une page autre que /owner.
//
// Bouton "Revenir owner" : appel best-effort à /api/owner/stop-impersonation
// (audit côté serveur), puis restauration du JWT owner depuis
// revyo_owner_token, nettoyage, reload vers /owner. Le reload franc évite
// toute reactivité résiduelle d'un cache de composant tenant l'identité
// impersonation en mémoire.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { storage } from '../utils/platform';
import { apiPost } from '../utils/api-client';

export default function ImpersonationBanner() {
  const location = useLocation();
  const { t } = useTranslation('dashboard');
  const [imp, setImp] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await storage.get('revyo_impersonation');
        if (cancelled) return;
        if (!raw) { setImp(null); return; }
        try {
          setImp(JSON.parse(raw));
        } catch {
          // Donnée corrompue → on nettoie silencieusement pour éviter un
          // état zombie qui bloquerait l'UI.
          await storage.remove('revyo_impersonation');
          setImp(null);
        }
      } catch {
        if (!cancelled) setImp(null);
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Pousse le contenu de la page vers le bas tant qu'une impersonation est
  // active, sinon le bandeau (position: fixed, ~44px de haut) masque la
  // première ligne de toutes les pages. Restaure la valeur précédente au
  // unmount pour ne pas laisser de padding zombie après "Revenir owner".
  useEffect(() => {
    if (!imp) return;
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = '44px';
    return () => { document.body.style.paddingTop = prev; };
  }, [imp]);

  if (!imp) return null;

  const handleStop = async () => {
    // Log côté serveur — best-effort, on ne bloque pas la sortie de
    // pilotage si l'appel échoue (le JWT impersonation expire de toute
    // façon en max 2h, et l'absence de log côté serveur n'a pas d'effet
    // sécu — juste un trou audit qu'on accepte volontairement).
    try { await apiPost('/api/owner/stop-impersonation'); } catch {}

    const ownerTok = await storage.get('revyo_owner_token');
    if (ownerTok) await storage.set('token', ownerTok);
    await storage.remove('revyo_owner_token');
    await storage.remove('revyo_impersonation');
    try { localStorage.removeItem('revyo_current_restaurant_id'); } catch {}

    // Reload franc : on quitte une UI montée avec l'identité impersonation
    // et on rebascule sur /owner avec le JWT owner restauré, sans tenter
    // de re-render React (risque d'écrans incohérents pendant 1 frame).
    window.location.href = '/owner';
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: '#E96A1B',
        color: '#FFFFFF',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        fontSize: '14px',
        lineHeight: 1.35,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        <span aria-hidden="true">🎛️</span>
        <strong style={{ flexShrink: 0 }}>{t('impersonation.mode_label')}</strong>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {imp.nom || t('impersonation.default_account')}
        </span>
      </div>
      <button
        type="button"
        onClick={handleStop}
        style={{
          flexShrink: 0,
          background: '#FFFFFF',
          color: '#E96A1B',
          border: 'none',
          borderRadius: '6px',
          padding: '6px 12px',
          fontWeight: 600,
          fontSize: '13px',
          cursor: 'pointer',
        }}
      >
        {t('impersonation.stop_button')}
      </button>
    </div>
  );
}
